import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { EmployeeIDBadge, DeviceIDBadge, CriticalityBadge } from '../../../shared/components/common/Badges';
import { AvailabilityImpactBadge } from '../../../shared/components/common/AvailabilityImpactBadge';
import { deviceHasAvailabilityImpact } from '../../../shared/constants/availabilityImpactFilter';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { formatPhoneNumber, removeCountryCode, validateIndianPhoneNumber } from '../../../utils/phoneFormatter';
import ComplaintPhotoUpload, {
  uploadComplaintPhotos,
  buildConfirmationPhotoThumbnails,
  revokePhotoPreviews,
} from './ComplaintPhotoUpload';
import useBodyScrollLock from '../../../shared/hooks/useBodyScrollLock';
import ModalCloseButton from '../../../shared/components/modals/ModalCloseButton';
import './ComplaintForm.css';

const sanitizeIndianPhoneInput = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 10);

  if (digits.length === 1 && digits === '0') {
    return '';
  }
  if (digits.length > 1 && digits.startsWith('0')) {
    return '';
  }

  return digits;
};

function ComplaintForm({ device, token, onClose, onSuccess, user }) {
  useBodyScrollLock(true);

  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const isCentralHelpDesk = user?.role === 'central_help_desk';
  const orgIdForLookup =
    device?.organization_id ||
    device?.organizationId ||
    user?.organization_id ||
    user?.organizationId ||
    null;
  const locationIdForLookup =
    device?.location_id ||
    device?.locationId ||
    device?.location?.id ||
    device?.location?.location_id ||
    null;

  const [formData, setFormData] = useState({
    category_id: '',
    title: '',
    problem_details: '',
    reporter_employee_id: isCentralHelpDesk ? '' : (user?.employee_id || ''),
    reporter_full_name: isCentralHelpDesk ? '' : (user?.full_name || ''),
    reporter_email: isCentralHelpDesk ? '' : (user?.email || ''),
    reporter_contact_number: isCentralHelpDesk ? '' : removeCountryCode(user?.contact_number || ''),
    breakdown_since: getCurrentDate(),  // Auto-set to current date
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photoError, setPhotoError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const selectedCategory = categories.find((c) => String(c.id) === String(formData.category_id));
  const isSystemCategorySelected = !!selectedCategory?.is_system;
  const [currentUser, setCurrentUser] = useState(user);
  const [employeeVerification, setEmployeeVerification] = useState({
    status: 'idle', // idle, loading, found, not_found
    error: null,
    data: null
  });
  const [verificationTimeout, setVerificationTimeout] = useState(null);
  const shouldGateSubmitByReporter =
    !user || isCentralHelpDesk;
  const isReporterInvalidForSubmit =
    shouldGateSubmitByReporter &&
    (
      employeeVerification.status === 'loading' ||
      employeeVerification.status === 'invalid_location' ||
      !!employeeVerification.error
    );

  // Fetch current user data to ensure we have latest contact info
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.AUTH.ME, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setCurrentUser(response.data);
        if (!isCentralHelpDesk) {
          setFormData(prev => ({
            ...prev,
            reporter_employee_id: response.data?.employee_id || prev.reporter_employee_id,
            reporter_full_name: response.data?.full_name || prev.reporter_full_name,
            reporter_email: response.data?.email || prev.reporter_email,
            reporter_contact_number: removeCountryCode(response.data?.contact_number || prev.reporter_contact_number)
          }));
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Fall back to passed user prop if fetch fails
        setCurrentUser(user);
      }
    };

    fetchUserData();
  }, [token, user, isCentralHelpDesk]);

  // Fetch complaint categories applicable to this device
  useEffect(() => {
    const fetchCategories = async () => {
      if (!device?.id) {
        setCategoriesLoading(false);
        return;
      }
      setCategoriesLoading(true);
      setCategoriesError(null);
      try {
        const response = await axios.get(
          API_ENDPOINTS.COMPLAINT_CATEGORIES.APPLICABLE(`device_id=${device.id}`),
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setCategories(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching complaint categories:', error);
        setCategoriesError('Failed to load complaint categories');
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, [device?.id, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    if (name === 'reporter_contact_number') {
      finalValue = sanitizeIndianPhoneInput(value);
    }

    if (name === 'category_id') {
      const previousCategory = categories.find((c) => String(c.id) === String(formData.category_id));
      const nextCategory = categories.find((c) => String(c.id) === String(finalValue));
      const previousLabel = (previousCategory?.label || '').trim();
      const nextLabel = (nextCategory?.label || '').trim();
      const titleWasAutoOrEmpty =
        !formData.title.trim() ||
        formData.title.trim() === previousLabel;

      setFormData((prev) => {
        let nextTitle = prev.title;
        if (!finalValue) {
          nextTitle = titleWasAutoOrEmpty ? '' : prev.title;
        } else if (nextCategory?.is_system) {
          // Other — user must enter a custom title
          nextTitle = titleWasAutoOrEmpty ? '' : prev.title;
        } else if (titleWasAutoOrEmpty) {
          nextTitle = nextLabel;
        }
        return {
          ...prev,
          category_id: finalValue,
          title: nextTitle,
        };
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));

    // Trigger employee verification when employee_id changes (guest + central help desk)
    if (name === 'reporter_employee_id' && (!user || isCentralHelpDesk)) {
      // Clear previous employee details when user changes the ID
      setFormData(prev => ({
        ...prev,
        reporter_full_name: '',
        reporter_email: '',
        reporter_contact_number: ''
      }));
      verifyEmployee(value);
    }
  };

  const verifyEmployee = (employeeId) => {
    // Clear previous timeout
    if (verificationTimeout) {
      clearTimeout(verificationTimeout);
    }

    if (!orgIdForLookup) {
      setEmployeeVerification({
        status: 'idle',
        error: 'Organization context missing - cannot verify employee',
        data: null
      });
      return;
    }

    if (!locationIdForLookup) {
      setEmployeeVerification({
        status: 'idle',
        error: 'Location context missing - cannot verify employee',
        data: null
      });
      return;
    }

    if (!employeeId || employeeId.trim() === '') {
      setEmployeeVerification({
        status: 'idle',
        error: null,
        data: null
      });
      return;
    }

    // Set loading state
    setEmployeeVerification({
      status: 'loading',
      error: null,
      data: null
    });

    // Debounce the API call (wait 500ms after user stops typing)
    const timeout = setTimeout(async () => {
      try {
        const response = await axios.get(
          API_ENDPOINTS.USERS.LOOKUP_BY_EMPLOYEE_ID(employeeId.trim()),
          {
            headers: {
              'X-Organization-Id': String(orgIdForLookup),
              'X-Location-Id': String(locationIdForLookup)
            }
          }
        );

        if (response.data.found) {
          // Employee found - auto-populate their details
          const contactNumber = response.data.contact_number ? removeCountryCode(response.data.contact_number) : '';
          setFormData(prev => ({
            ...prev,
            reporter_employee_id: response.data.employee_id || prev.reporter_employee_id,
            reporter_full_name: response.data.full_name,
            reporter_email: response.data.email,
            reporter_contact_number: contactNumber
          }));

          setEmployeeVerification({
            status: 'found',
            error: null,
            data: response.data
          });
        } else {
          setEmployeeVerification({
            status: 'not_found',
            error: null,
            data: null
          });
        }
      } catch (error) {
        if (error.response?.status === 404) {
          setEmployeeVerification({
            status: 'not_found',
            error: null,
            data: null
          });
        } else if (error.response?.status === 400) {
          const backendMessage = error.response?.data?.message || error.response?.data?.error;
          setEmployeeVerification({
            status: 'invalid_location',
            error: backendMessage || 'Employee not valid for this location',
            data: null
          });
        } else {
          setEmployeeVerification({
            status: 'idle',
            error: 'Error verifying employee',
            data: null
          });
          console.error('Error verifying employee:', error);
        }
      }
    }, 500);

    setVerificationTimeout(timeout);
  };

  const validateForm = () => {
    if (!formData.category_id) {
      return { ok: false, message: 'Please select a complaint category' };
    }
    if (isSystemCategorySelected) {
      if (!formData.title.trim()) {
        return { ok: false, message: 'Please enter a title' };
      }
      if (!formData.problem_details.trim()) {
        return { ok: false, message: 'Please enter details' };
      }
    }

    // Reporter validation for guest + central help desk flows
    if (!user || isCentralHelpDesk) {
      if (!formData.reporter_employee_id.trim()) {
        return { ok: false, message: 'Please enter the reporter employee ID' };
      }
      if (employeeVerification.status === 'loading') {
        return { ok: false, message: 'Please wait for employee verification to complete' };
      }
      if (employeeVerification.error) {
        return { ok: false, message: employeeVerification.error };
      }
      // If employee not found, manual details are required
      if (employeeVerification.status !== 'found') {
        if (!formData.reporter_full_name.trim() || !formData.reporter_email.trim() || !formData.reporter_contact_number.trim()) {
          return { ok: false, message: 'Please provide reporter name, email, and contact number' };
        }

        const phoneValidation = validateIndianPhoneNumber(formData.reporter_contact_number);
        if (!phoneValidation.isValid) {
          return { ok: false, message: phoneValidation.error || 'Please enter a valid 10-digit contact number' };
        }
      }
    }

    return { ok: true };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    // Validate form before submission
    const validation = validateForm();
    if (!validation.ok) {
      if (validation.message) {
        setSubmitError(validation.message);
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(API_ENDPOINTS.COMPLAINTS.CREATE, {
        device_id: device.id,
        ...formData
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let photoUploadWarning = null;
      let attachedPhotos = [];
      if (photos.length > 0 && response.data?.id) {
        const { failed, succeeded } = await uploadComplaintPhotos(response.data.id, photos, {
          token,
          photoUploadToken: response.data.photo_upload_token,
        });
        if (failed.length > 0) {
          photoUploadWarning = `Complaint created, but ${failed.length} photo(s) failed to upload.`;
        }
        attachedPhotos = await buildConfirmationPhotoThumbnails(succeeded);
        revokePhotoPreviews(photos);
        setPhotos([]);
      }

      const confirmationPayload = {
        id: response.data?.id,
        complaint_identifier: response.data?.complaint_identifier,
        photoUploadWarning,
        attachedPhotos,
        reporter: {
          employee_id: formData.reporter_employee_id,
          full_name: formData.reporter_full_name || currentUser?.full_name || user?.full_name,
          email: formData.reporter_email || currentUser?.email || user?.email,
          contact_number: formData.reporter_contact_number || currentUser?.contact_number || user?.contact_number,
          role: response.data?.reporter_role || (!isCentralHelpDesk ? (currentUser?.role || user?.role) : employeeVerification.data?.role)
        },
        assignedTechnician: {
          employee_id: response.data?.assigned_employee_id,
          full_name: response.data?.assigned_employee_name,
          email: response.data?.assigned_employee_email,
          contact_number: response.data?.assigned_employee_contact,
          role: response.data?.assigned_employee_role
        },
        device
      };

      if (onSuccess) onSuccess(confirmationPayload);
      onClose();
    } catch (error) {
      console.error('Error creating complaint:', error);
      const message =
        error.response?.data?.error ||
        'Failed to create complaint. Please try again.';
      setSubmitError(message);
      setIsSubmitting(false);
    }
  };

  const renderPhoneValidationFeedback = () => {
    const digits = formData.reporter_contact_number ? formData.reporter_contact_number.replace(/\D/g, '') : '';
    const validation = formData.reporter_contact_number
      ? validateIndianPhoneNumber(formData.reporter_contact_number)
      : { isValid: true, error: null };
    const statusColor = formData.reporter_contact_number && !validation.isValid
      ? '#d32f2f'
      : digits.length === 10
        ? '#27ae60'
        : '#f59e0b';

    return (
      <>
        <div className="phone-validation-display">
          <span className="digit-count" style={{ color: statusColor }}>
            {digits.length}/10
          </span>
          {formData.reporter_contact_number && (
            <span className="validation-status" style={{ color: statusColor }}>
              {!validation.isValid ? '✗' : digits.length === 10 ? '✓' : '⚠'}
            </span>
          )}
        </div>
        {formData.reporter_contact_number && validation.error && (
          <small className="form-error-text" style={{ color: '#d32f2f' }}>
            {validation.error}
          </small>
        )}
        {formData.reporter_contact_number && validation.isValid && (
          <small className="form-success-text" style={{ color: '#27ae60' }}>
            ✓ {formatPhoneNumber(formData.reporter_contact_number)}
          </small>
        )}
        <small className="form-help-text">Indian format: 10-digit mobile number (must start with 6, 7, 8, or 9)</small>
      </>
    );
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className="complaint-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-extended">
          <div className="modal-header-title">
            <h3>Report Device Breakdown</h3>
            <div className="modal-device-info">
              <div className="modal-device-title-row">
                <span className="device-name">{device.name}</span>
                <DeviceIDBadge deviceId={device.device_identifier || device.device_id} size="small" />
              </div>
              <div className="modal-device-badges">
                {device.criticality && (
                  <CriticalityBadge criticality={device.criticality} size="small" />
                )}
                {deviceHasAvailabilityImpact(device) && (
                  <AvailabilityImpactBadge deviceHasImpact size="small" />
                )}
                {device.department && (
                  <span className="header-department">
                    <span className="department-icon" aria-hidden="true">
                      {device.department_icon || '🏥'}
                    </span>
                    <span className="device-department">
                      <span className="department-name">{device.department}</span>
                    </span>
                  </span>
                )}
                {device.physical_location && (
                  <span className="device-location">
                    <span className="location-icon" aria-hidden="true">📍</span>
                    <span className="location-name">{device.physical_location}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} title="Close" />
        </div>

        <form onSubmit={handleSubmit} className="complaint-form">
          <div className="form-group">
            <label htmlFor="category_id">Category *</label>
            <select
              id="category_id"
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              required
              disabled={categoriesLoading}
              autoFocus
            >
              <option value="">
                {categoriesLoading ? 'Loading categories...' : 'Select a category'}
              </option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>{category.label}</option>
              ))}
            </select>
            {categoriesError && (
              <small className="form-error-text" style={{ color: '#d32f2f' }}>{categoriesError}</small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="title">Title {isSystemCategorySelected ? '*' : ''}</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required={isSystemCategorySelected}
              placeholder="e.g., Device not powering on"
            />
            {!isSystemCategorySelected && formData.category_id ? (
              <small className="form-help-text">Pre-filled from category — you can change it if needed</small>
            ) : isSystemCategorySelected ? (
              <small className="form-help-text">Enter a short title</small>
            ) : null}
          </div>

          <div className="form-group">
            <label htmlFor="problem_details">Details {isSystemCategorySelected ? '*' : ''}</label>
            <textarea
              id="problem_details"
              name="problem_details"
              value={formData.problem_details}
              onChange={handleChange}
              required={isSystemCategorySelected}
              rows="4"
              placeholder="Describe the issue in detail..."
            />
          </div>

          {/* Hidden Breakdown Since field - auto-set to current date */}
          <input
            type="hidden"
            id="breakdown_since"
            name="breakdown_since"
            value={formData.breakdown_since}
          />

          {/* AUTHENTICATED REPORTER SECTION - Hospital Staff */}
          {user && !isCentralHelpDesk ? (
            <>
              <div className="reporter-flow-indicator">
                <span className="flow-badge authenticated">✓ Authenticated</span>
                <span className="flow-description">Hospital staff member reporting issue</span>
              </div>
              <div className="form-group">
                <label>Reported By *</label>
                <div className="reporter-info-display">
                  <div className="reporter-info-badge">
                    <EmployeeIDBadge employeeId={formData.reporter_employee_id} size="medium" />
                  </div>
                  <div className="reporter-details">
                    {currentUser?.full_name && (
                      <div className="reporter-detail-item">
                        <span className="detail-icon">👤</span>
                        <span className="detail-text">{currentUser.full_name}</span>
                      </div>
                    )}
                    {currentUser?.email && (
                      <div className="reporter-detail-item">
                        <span className="detail-icon">✉️</span>
                        <span className="detail-text">{currentUser.email}</span>
                      </div>
                    )}
                    {formData.reporter_contact_number && (
                      <div className="reporter-detail-item">
                        <span className="detail-icon">📞</span>
                        <span className="detail-text">{formatPhoneNumber(formData.reporter_contact_number)}</span>
                      </div>
                    )}
                    {!formData.reporter_contact_number && (
                      <div className="reporter-detail-item reporter-detail-empty">
                        <span className="detail-icon">📞</span>
                        <span className="detail-text detail-text-empty">Contact number not available</span>
                      </div>
                    )}
                  </div>
                  <small className="field-hint">✓ Auto-populated based on logged-in user</small>
                </div>
              </div>
            </>
          ) : (
            /* REPORTER SECTION - Guest or Central Help Desk (caller details) */
            <>
              <div className="reporter-flow-indicator">
                <span className={`flow-badge ${isCentralHelpDesk ? 'authenticated' : 'guest'}`}>
                  {isCentralHelpDesk ? '☎ Central Help Desk' : '👤 Guest'}
                </span>
                <span className="flow-description">
                  {isCentralHelpDesk
                    ? 'Help desk agent logging a complaint on behalf of the caller'
                    : 'Public user reporting issue via QR code'}
                </span>
              </div>
              <div className="reporter-identification-section">
              <div className="section-header">👤 Reporter Information *</div>

              {/* Employee ID Input */}
              <div className="form-group">
                <label htmlFor="reporter_employee_id">Employee ID *</label>
                <input
                  type="text"
                  id="reporter_employee_id"
                  name="reporter_employee_id"
                  value={formData.reporter_employee_id}
                  onChange={handleChange}
                  placeholder="e.g., DHI000001"
                  required
                  className={`employee-id-input ${
                    employeeVerification.status === 'loading' ? 'loading' : ''
                  } ${employeeVerification.status === 'found' ? 'found' : ''} ${
                    employeeVerification.status === 'not_found' ? 'not-found' : ''
                  }`}
                />

                {/* Employee Verification Indicator */}
                {employeeVerification.status === 'loading' && (
                  <div className="verification-indicator loading">⟳ Verifying...</div>
                )}
                {employeeVerification.status === 'found' && (
                  <div className="verification-indicator found">✓ Employee found - details auto-populated</div>
                )}
                {employeeVerification.status === 'not_found' && !employeeVerification.error && (
                  <div className="verification-indicator not-found">
                    ⓘ Employee not in system - please enter details manually
                  </div>
                )}
                {employeeVerification.error && (
                  <div className="verification-indicator not-found">
                    ⓘ {employeeVerification.error}
                  </div>
                )}
              </div>

              {/* If employee not found, show manual entry fields */}
              {employeeVerification.status !== 'found' && employeeVerification.status !== 'invalid_location' && (
                <div className="manual-entry-fields">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="reporter_full_name">Full Name *</label>
                      <input
                        type="text"
                        id="reporter_full_name"
                        name="reporter_full_name"
                        value={formData.reporter_full_name}
                        onChange={handleChange}
                        placeholder="e.g., John Doe"
                        required={employeeVerification.status !== 'found'}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="reporter_email">Email *</label>
                      <input
                        type="email"
                        id="reporter_email"
                        name="reporter_email"
                        value={formData.reporter_email}
                        onChange={handleChange}
                        placeholder="e.g., john@example.com"
                        required={employeeVerification.status !== 'found'}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="reporter_contact_number">Contact Number *</label>
                    <div className="phone-input-wrapper">
                      <input
                        type="tel"
                        id="reporter_contact_number"
                        name="reporter_contact_number"
                        value={formData.reporter_contact_number}
                        onChange={handleChange}
                        placeholder="e.g., 9876543210"
                        required
                        maxLength="10"
                      />
                      {renderPhoneValidationFeedback()}
                    </div>
                  </div>
                </div>
              )}

              {/* If employee found, show read-only display */}
              {employeeVerification.status === 'found' && (
                <div className="employee-details-found">
                  <div className="detail-row">
                    <span className="detail-label">👤 Name:</span>
                    <span className="detail-value">{formData.reporter_full_name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">✉️ Email:</span>
                    <span className="detail-value">{formData.reporter_email}</span>
                  </div>
                  {formData.reporter_contact_number && (
                    <div className="detail-row">
                      <span className="detail-label">📞 Contact:</span>
                      <span className="detail-value">{formatPhoneNumber(formData.reporter_contact_number)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            </>
          )}

          {submitError && (
            <div className="complaint-submit-error" role="alert">
              {submitError}
            </div>
          )}

          <ComplaintPhotoUpload
            photos={photos}
            disabled={isSubmitting}
            onChange={(next, err) => {
              setPhotos(next);
              setPhotoError(err);
            }}
          />
          {photoError && (
            <div className="complaint-submit-error" role="alert">{photoError}</div>
          )}

          <div className="form-actions">
            <button type="button" className="action-btn action-secondary" onClick={onClose} disabled={isSubmitting}>
              <span className="action-icon">✕</span>
              <span className="action-text">Cancel</span>
            </button>
            <button
              type="submit"
              className="action-btn action-primary"
              disabled={isSubmitting || isReporterInvalidForSubmit}
              title={isReporterInvalidForSubmit ? 'Please resolve reporter validation before submitting' : undefined}
            >
              <span className="action-icon">📝</span>
              <span className="action-text">{isSubmitting ? 'Submitting...' : 'Report Breakdown'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default ComplaintForm;
